import { MONEY_LEVELS, type MoneyLevel, type MoneyScopeInput } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { directionsQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Select } from '../../ui/form';

/**
 * Деньги по проектам (ДС-18): сверх общего уровня владелец компании выдаёт уровень на проект (с подпроектами),
 * направление или юрлицо. Действует наибольший из общего и уровня области. Видит и меняет только владелец.
 * Внешнему участнику — только его проекты (`projectIds`), не выше «Акты и счета».
 */
export function MoneyScopesEditor({
  value,
  onChange,
  projectIds,
}: {
  value: MoneyScopeInput[];
  onChange: (v: MoneyScopeInput[]) => void;
  /** Внешний участник: только эти проекты */
  projectIds?: string[];
}) {
  const me = useMe();
  const dirs = useQuery(directionsQuery);
  const projects = useQuery(projectsQuery('all'));
  const external = Boolean(projectIds);
  const key = (s: Pick<MoneyScopeInput, 'scopeType' | 'scopeId'>) => `${s.scopeType}:${s.scopeId}`;
  const used = new Set(value.map(key));
  const projectList = (projects.data ?? []).filter((p) => !projectIds || projectIds.includes(p.id));
  const options: Array<{ group: string; key: string; label: string }> = [
    ...(external
      ? []
      : me.legalEntities.map((le) => ({
          group: 'Юрлица',
          key: `legal_entity:${le.id}`,
          label: `Юрлицо ${le.shortName}`,
        }))),
    ...(external
      ? []
      : (dirs.data ?? []).map((d) => ({ group: 'Направления', key: `direction:${d.id}`, label: d.name }))),
    ...projectList.map((p) => ({
      group: 'Проекты и подпроекты',
      key: `project:${p.id}`,
      label: `${p.parentId ? '— ' : ''}${p.code} ${p.name}`,
    })),
  ];
  const levels = (Object.keys(MONEY_LEVELS) as MoneyLevel[]).filter(
    (l) => l !== 'none' && (!external || l !== 'all'),
  );

  function update(i: number, patch: Partial<MoneyScopeInput>) {
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }
  function addRow() {
    // Чаще всего уровень дают на проект: сначала свободный проект, потом направление или юрлицо
    const free =
      options.find((o) => o.key.startsWith('project:') && !used.has(o.key)) ??
      options.find((o) => !used.has(o.key));
    if (!free) return;
    const [scopeType, scopeId] = free.key.split(':') as [MoneyScopeInput['scopeType'], string];
    onChange([...value, { scopeType, scopeId, moneyLevel: levels.at(-1)! }]);
  }

  return (
    <div className="flex flex-col gap-2">
      {value.length === 0 ? (
        <p className="text-[13px] text-ink-2">
          Нет. Добавьте проект, направление или юрлицо, где человек должен видеть больше, чем по общему
          уровню.
        </p>
      ) : null}
      {value.map((s, i) => (
        <div key={key(s)} className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[1.5fr_1fr_auto]">
          <Select
            aria-label="Где действует уровень денег"
            value={key(s)}
            className="col-span-2 sm:col-span-1"
            onChange={(e) => {
              const [scopeType, scopeId] = e.target.value.split(':') as [
                MoneyScopeInput['scopeType'],
                string,
              ];
              update(i, { scopeType, scopeId });
            }}
          >
            {/* Сохранённая область показывается, даже если справочник ещё не загрузился */}
            {options.some((o) => o.key === key(s)) ? null : <option value={key(s)}>текущая область</option>}
            {['Юрлица', 'Направления', 'Проекты и подпроекты'].map((g) =>
              options.some((o) => o.group === g) ? (
                <optgroup key={g} label={g}>
                  {options
                    .filter((o) => o.group === g && (o.key === key(s) || !used.has(o.key)))
                    .map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                </optgroup>
              ) : null,
            )}
          </Select>
          <Select
            aria-label="Деньги здесь"
            value={s.moneyLevel}
            onChange={(e) => update(i, { moneyLevel: e.target.value as MoneyLevel })}
          >
            {levels.map((l) => (
              <option key={l} value={l}>
                {MONEY_LEVELS[l]}
              </option>
            ))}
          </Select>
          <IconButton label="Убрать" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      <div>
        <Button
          size="sm"
          variant="ghost"
          icon={<Plus className="size-4" />}
          disabled={!options.some((o) => !used.has(o.key))}
          onClick={addRow}
        >
          Добавить проект
        </Button>
      </div>
    </div>
  );
}
